import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
  IconButton,
  Alert,
  Divider,
  Card,
  CardContent
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Timer as TimerIcon,
  Quiz as QuizIcon,
  MusicNote as MusicIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Styled components
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    backgroundColor: '#1A1A1A',
    border: '1px solid #333',
    borderRadius: '12px',
    maxWidth: '700px',
    width: '90vw',
    maxHeight: '90vh',
  },
}));

const InfoCard = styled(Card)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.02)',
  border: '1px solid #333',
  borderRadius: '8px',
  marginBottom: '16px',
}));

const AnswerCard = styled(Card)(({ theme, isCorrect }) => ({
  backgroundColor: isCorrect ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255, 255, 255, 0.02)',
  border: `1px solid ${isCorrect ? '#4CAF50' : '#333'}`,
  borderRadius: '8px',
  marginBottom: '12px',
}));

const MetadataBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 12px',
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  borderRadius: '6px',
  marginBottom: '8px',
}));

const QuestionViewModal = ({ 
  open, 
  onClose, 
  question, 
  validationResult,
  lookupData,
  songData 
}) => {
  if (!question) return null;

  // Get lookup display names
  const getQuestionBankName = () => {
    const bank = lookupData?.questionBanks?.find(b => b.bank_id === question.questionBank);
    return bank?.bank_name || 'Unknown';
  };

  const getDifficultyName = () => {
    const difficulty = lookupData?.difficulties?.find(d => d.difficulty_id === question.difficulty);
    return difficulty?.level_name || 'Unknown';
  };

  const getQuestionTypeName = () => {
    const type = lookupData?.questionTypes?.find(t => t.type_name === question.questionType);
    return type?.display_name || question.questionType;
  };

  const getAnswerTypeName = () => {
    const type = lookupData?.answerTypes?.find(t => t.type_name === question.answerType);
    return type?.display_name || question.answerType;
  };

  const getMoodName = () => {
    const mood = lookupData?.questionMoods?.find(m => m.mood_type_id === question.questionMood);
    return mood?.display_name || 'Unknown';
  };

  const getTopicName = () => {
    const topic = lookupData?.questionTopics?.find(t => t.topic_type_id === question.questionTopic);
    return topic?.display_name || 'Unknown';
  };

  const correctAnswersCount = question.answers?.filter(a => a.correct).length || 0;
  const isValid = validationResult?.isValid ?? true;

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ 
        color: '#fff', 
        borderBottom: '1px solid #333',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <QuizIcon sx={{ color: '#4CAF50' }} />
          <Typography variant="h6">Question Preview</Typography>
          {!isValid && (
            <Chip 
              label="Has Errors" 
              size="small" 
              sx={{ backgroundColor: '#f44336', color: '#fff' }}
            />
          )}
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#ccc' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Validation Status */}
        {!isValid && validationResult?.errors && (
          <Alert severity="error" sx={{ mb: 3, backgroundColor: 'rgba(244, 67, 54, 0.1)' }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Validation Errors:</Typography>
            {validationResult.errors.map((error, index) => (
              <Typography key={index} variant="body2">• {error}</Typography>
            ))}
          </Alert>
        )}

        {/* Question Content */}
        <InfoCard>
          <CardContent>
            <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
              Question Text
            </Typography>
            <Typography variant="body1" sx={{ color: '#ccc', lineHeight: 1.6 }}>
              {question.questionText}
            </Typography>
          </CardContent>
        </InfoCard>

        {/* Question Metadata */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <MetadataBox>
              <TimerIcon sx={{ color: '#4CAF50', fontSize: 20 }} />
              <Typography variant="body2" sx={{ color: '#fff' }}>
                Time Limit: {question.timeLimit || 30} seconds
              </Typography>
            </MetadataBox>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <MetadataBox>
              <MusicIcon sx={{ color: '#4CAF50', fontSize: 20 }} />
              <Typography variant="body2" sx={{ color: '#fff' }}>
                Song: {songData?.name || 'Unknown'}
              </Typography>
            </MetadataBox>
          </Grid>

          <Grid item xs={12} md={6}>
            <MetadataBox>
              <CategoryIcon sx={{ color: '#4CAF50', fontSize: 20 }} />
              <Typography variant="body2" sx={{ color: '#fff' }}>
                Bank: {getQuestionBankName()}
              </Typography>
            </MetadataBox>
          </Grid>

          <Grid item xs={12} md={6}>
            <MetadataBox>
              <CategoryIcon sx={{ color: '#4CAF50', fontSize: 20 }} />
              <Typography variant="body2" sx={{ color: '#fff' }}>
                Difficulty: {getDifficultyName()}
              </Typography>
            </MetadataBox>
          </Grid>
        </Grid>

        {/* Question Clue */}
        {question.questionClue && (
          <InfoCard>
            <CardContent>
              <Typography variant="subtitle2" sx={{ color: '#4CAF50', mb: 1 }}>
                Question Clue
              </Typography>
              <Typography variant="body2" sx={{ color: '#ccc' }}>
                {question.questionClue}
              </Typography>
            </CardContent>
          </InfoCard>
        )}

        <Divider sx={{ my: 3, borderColor: '#333' }} />

        {/* Answers Section */}
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ color: '#fff' }}>
              Answers ({question.answers?.length || 0})
            </Typography>
            <Chip 
              label={`${correctAnswersCount} correct`}
              size="small"
              sx={{ 
                backgroundColor: correctAnswersCount > 0 ? '#4CAF50' : '#f44336',
                color: '#fff'
              }}
            />
          </Box>

          {question.answers?.map((answer, index) => (
            <AnswerCard key={index} isCorrect={answer.correct}>
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 'fit-content' }}>
                    {answer.correct ? (
                      <CheckIcon sx={{ color: '#4CAF50', fontSize: 20 }} />
                    ) : (
                      <CancelIcon sx={{ color: '#f44336', fontSize: 20 }} />
                    )}
                    <Typography variant="subtitle2" sx={{ color: '#4CAF50' }}>
                      {String.fromCharCode(65 + index)}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" sx={{ color: '#fff', mb: 1 }}>
                      {answer.text}
                    </Typography>
                    {answer.reason && (
                      <Typography variant="body2" sx={{ color: '#ccc', fontStyle: 'italic' }}>
                        Reason: {answer.reason}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </AnswerCard>
          ))}
        </Box>

        <Divider sx={{ my: 3, borderColor: '#333' }} />

        {/* Additional Settings */}
        <Box>
          <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
            Question Settings
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ color: '#ccc' }}>
                <strong>Question Type:</strong> {getQuestionTypeName()}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ color: '#ccc' }}>
                <strong>Answer Type:</strong> {getAnswerTypeName()}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ color: '#ccc' }}>
                <strong>Mood:</strong> {getMoodName()}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ color: '#ccc' }}>
                <strong>Topic:</strong> {getTopicName()}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: '1px solid #333' }}>
        <Button onClick={onClose} sx={{ color: '#4CAF50' }}>
          Close
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default QuestionViewModal;
